import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Divider,    InputAdornment,
    MenuItem,
    Switch,
    FormControlLabel,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailIcon from '@mui/icons-material/Email';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DevicesIcon from '@mui/icons-material/Devices';
import { API_BASE_URL } from '../config';

const CURRENCIES = [
    { code: 'USD', label: 'USD – US Dollar' },
    { code: 'EUR', label: 'EUR – Euro' },
    { code: 'GBP', label: 'GBP – British Pound' },
    { code: 'JPY', label: 'JPY – Japanese Yen' },
    { code: 'CAD', label: 'CAD – Canadian Dollar' },
    { code: 'AUD', label: 'AUD – Australian Dollar' },
    { code: 'CHF', label: 'CHF – Swiss Franc' },
    { code: 'ILS', label: 'ILS – Israeli Shekel' },
    { code: 'CNY', label: 'CNY – Chinese Yuan' },
    { code: 'INR', label: 'INR – Indian Rupee' },
    { code: 'BRL', label: 'BRL – Brazilian Real' },
    { code: 'MXN', label: 'MXN – Mexican Peso' },
    { code: 'SGD', label: 'SGD – Singapore Dollar' },
    { code: 'HKD', label: 'HKD – Hong Kong Dollar' },
    { code: 'NOK', label: 'NOK – Norwegian Krone' },
    { code: 'SEK', label: 'SEK – Swedish Krona' },
    { code: 'DKK', label: 'DKK – Danish Krone' },
    { code: 'NZD', label: 'NZD – New Zealand Dollar' },
    { code: 'ZAR', label: 'ZAR – South African Rand' },
    { code: 'AED', label: 'AED – UAE Dirham' },
];

const ProfileSettings = ({ open, onClose, user, onProfileUpdated }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    // Password change state
    const [currentPw, setCurrentPw] = useState(''); // kept for reset only
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [pwLoading, setPwLoading] = useState(false);
    const [pwError, setPwError] = useState('');
    const [pwSuccess, setPwSuccess] = useState('');

    // WhatsApp linking state
    const [linkStep, setLinkStep] = useState('idle'); // 'idle' | 'phone' | 'code'
    const [waPhone, setWaPhone] = useState('');
    const [waCode, setWaCode] = useState('');
    const [linkLoading, setLinkLoading] = useState(false);
    const [linkError, setLinkError] = useState('');
    const [linkSuccess, setLinkSuccess] = useState('');

    // Email credentials state
    const [emailCredentials, setEmailCredentials] = useState([]);
    const [emailsLoading, setEmailsLoading] = useState(false);
    // Add email via confirmation link
    const [newEmailInput, setNewEmailInput] = useState('');
    const [addEmailLoading, setAddEmailLoading] = useState(false);
    const [addEmailError, setAddEmailError] = useState('');
    const [addEmailSent, setAddEmailSent] = useState(false);
    // Popup dialog shown when the email is already linked to a DIFFERENT account
    const [emailConflictDialog, setEmailConflictDialog] = useState({ open: false, email: '' });
    // Track which credential is being deleted (to show spinner)
    const [deletingEmailId, setDeletingEmailId] = useState(null);

    // Single-device restriction state
    const [singleDevice, setSingleDevice] = useState(false);
    const [sessionSaving, setSessionSaving] = useState(false);
    const [sessionError, setSessionError] = useState('');

    // Only show WhatsApp linking for username/password users who don't have WhatsApp yet
    const isUsernameUser = user && user.login_method === 'email' && !user.has_whatsapp;
    const alreadyLinkedWa = user && user.has_whatsapp && user.phone_number;

    useEffect(() => {
        if (open && user) {
            setName(user.whatsapp_name || '');
            setCurrency(user.currency || 'USD');
            setError('');
            setSuccess('');
            setCurrentPw('');
            setNewPw('');
            setConfirmPw('');
            setPwError('');
            setPwSuccess('');
            setLinkStep('idle');
            setWaPhone('');
            setWaCode('');
            setLinkError('');
            setLinkSuccess('');
            setEmailCredentials([]);
            setNewEmailInput('');
            setAddEmailError('');
            setAddEmailSent(false);
            setEmailConflictDialog({ open: false, email: '' });
            setSingleDevice(!!user.single_device);
            setSessionError('');
            loadEmailCredentials();
        }
    }, [open, user]);

    const handleToggleSingleDevice = async (event) => {
        const enabled = event.target.checked;
        const previous = singleDevice;
        setSingleDevice(enabled);
        setSessionSaving(true);
        setSessionError('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/update_session_settings.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ single_device: enabled })
            });
            const data = await response.json();
            if (response.ok && data.success) {
                if (onProfileUpdated) {
                    onProfileUpdated({ ...user, single_device: data.single_device });
                }
            } else {
                setSingleDevice(previous);
                setSessionError(data.message || 'Failed to update setting');
            }
        } catch (err) {
            setSingleDevice(previous);
            setSessionError(t('network_error'));
        } finally {
            setSessionSaving(false);
        }
    };

    const handleSaveProfile = async () => {
        if (!name.trim()) {
            setError(t('name_required'));
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/update_profile.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ name: name.trim(), currency })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(t('profile_updated_success'));
                if (onProfileUpdated) {
                    onProfileUpdated({ ...user, whatsapp_name: name.trim(), currency });
                }
            } else {
                setError(data.message || t('profile_update_failed'));
            }
        } catch (err) {
            setError(t('network_error'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePassword = async () => {
        if (newPw !== confirmPw) {
            setPwError(t('passwords_do_not_match'));
            return;
        }
        if (newPw.length < 6) {
            setPwError(t('password_too_short'));
            return;
        }
        setPwLoading(true);
        setPwError('');
        setPwSuccess('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/change_password.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ new_password: newPw })
            });
            const data = await response.json();
            if (response.ok) {
                setPwSuccess(t('password_changed_success'));
                setCurrentPw('');
                setNewPw('');
                setConfirmPw('');
            } else {
                setPwError(data.message || t('server_error'));
            }
        } catch (err) {
            setPwError(t('network_error'));
        } finally {
            setPwLoading(false);
        }
    };

    const loadEmailCredentials = async () => {
        setEmailsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/get_credentials.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (response.ok && data.credentials) {
                setEmailCredentials(
                    data.credentials.filter(c => c.credential_type === 'email' && c.status !== 'Revoked' && (c.credential_masked || c.credential_value || '').includes('@'))
                );
            }
        } catch (e) {
            // silently fail
        } finally {
            setEmailsLoading(false);
        }
    };

    const handleDeleteEmail = async (credentialId) => {
        setDeletingEmailId(credentialId);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/delete_credential.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ credential_id: credentialId })
            });
            const data = await response.json();
            if (response.ok && data.success) {
                await loadEmailCredentials();
            } else {
                setAddEmailError(data.message || 'Failed to remove email');
            }
        } catch (e) {
            setAddEmailError('Failed to connect to server');
        } finally {
            setDeletingEmailId(null);
        }
    };

    const handleSendAddEmail = async () => {
        const trimmed = newEmailInput.trim().toLowerCase();
        setAddEmailError('');
        if (!trimmed) { setAddEmailError('Please enter an email address'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setAddEmailError('Please enter a valid email address'); return; }
        setAddEmailLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/initiate_email_confirmation.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ email: trimmed })
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setAddEmailSent(true);
            } else if (data.message && data.message.toLowerCase().includes('different account')) {
                // Show dedicated popup dialog for this specific conflict
                setEmailConflictDialog({ open: true, email: trimmed });
            } else {
                setAddEmailError(data.message || 'Failed to send confirmation email');
            }
        } catch (err) {
            setAddEmailError('Failed to connect to server');
        } finally {
            setAddEmailLoading(false);
        }
    };

    const handleSendWhatsAppCode = async () => {
        const phone = waPhone.trim();
        if (!phone) {
            setLinkError(t('enter_whatsapp_number'));
            return;
        }
        setLinkLoading(true);
        setLinkError('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/send_linking_code.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ type: 'whatsapp', value: phone })
            });
            const data = await response.json();
            if (response.ok) {
                setLinkStep('code');
            } else {
                setLinkError(data.message || t('server_error'));
            }
        } catch (err) {
            setLinkError(t('network_error'));
        } finally {
            setLinkLoading(false);
        }
    };

    const handleVerifyWhatsAppCode = async () => {
        if (waCode.length !== 6) {
            setLinkError(t('enter_verification_code'));
            return;
        }
        setLinkLoading(true);
        setLinkError('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/verify_linking_code.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ type: 'whatsapp', value: waPhone.trim(), code: waCode })
            });
            const data = await response.json();
            if (response.ok) {
                setLinkSuccess(t('whatsapp_linked_success'));
                setLinkStep('idle');
                setWaPhone('');
                setWaCode('');
                if (onProfileUpdated) {
                    // Re-fetch full user from me.php so credentials array is up to date
                    try {
                        const meResp = await fetch(`${API_BASE_URL}/me.php`, {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                        });
                        const meData = await meResp.json();
                        if (meData.user) {
                            onProfileUpdated(meData.user);
                        } else {
                            onProfileUpdated({ ...user, has_whatsapp: true, phone_number: data.user?.phone_number || waPhone.trim() });
                        }
                    } catch (_) {
                        onProfileUpdated({ ...user, has_whatsapp: true, phone_number: data.user?.phone_number || waPhone.trim() });
                    }
                }
            } else {
                setLinkError(data.message || t('invalid_verification_code'));
            }
        } catch (err) {
            setLinkError(t('network_error'));
        } finally {
            setLinkLoading(false);
        }
    };

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('profile_settings')}</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {/* Name field */}
                    <TextField
                        fullWidth
                        label={t('name_label')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        margin="normal"
                        disabled={isLoading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonOutlineIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Username field - read only */}
                    {user?.username && (
                        <TextField
                            fullWidth
                            label={t('username_label')}
                            value={user.username || ''}
                            margin="normal"
                            disabled
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}

                    <Divider sx={{ my: 3 }} />

                    {/* Email Address Section */}
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Linked Email Addresses
                    </Typography>

                    {emailsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : emailCredentials.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            No email addresses linked yet.
                        </Typography>
                    ) : (
                        <Box sx={{ mb: 2 }}>
                            {emailCredentials.map((cred) => (
                                <Box
                                    key={cred.credential_id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        py: 0.75,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '&:last-child': { borderBottom: 'none' },
                                    }}
                                >
                                    <EmailIcon sx={{ color: '#1976d2', fontSize: 20, mr: 1.5, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {cred.credential_masked || cred.credential_value}
                                    </Typography>
                                    <button
                                        onClick={() => handleDeleteEmail(cred.credential_id)}
                                        disabled={deletingEmailId === cred.credential_id}
                                        aria-label="Remove email"
                                        style={{
                                            flexShrink: 0,
                                            marginLeft: 8,
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '8px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#d32f2f',
                                            WebkitTapHighlightColor: 'transparent',
                                            opacity: deletingEmailId === cred.credential_id ? 0.5 : 1,
                                            minWidth: 36,
                                            minHeight: 36,
                                        }}
                                    >
                                        {deletingEmailId === cred.credential_id
                                            ? <CircularProgress size={18} />
                                            : <DeleteOutlineIcon style={{ display: 'block', color: '#d32f2f', fontSize: 22 }} />}
                                    </button>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {addEmailSent ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Confirmation email sent to <strong>{newEmailInput.trim()}</strong>. Check your inbox and click the link — it expires in 24 hours.
                        </Alert>
                    ) : (
                        <>
                            {addEmailError && (
                                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setAddEmailError('')}>
                                    {addEmailError}
                                </Alert>
                            )}
                            <TextField
                                fullWidth
                                label="New Email Address"
                                type="email"
                                value={newEmailInput}
                                onChange={(e) => setNewEmailInput(e.target.value)}
                                placeholder="you@example.com"
                                size="small"
                                sx={{ mb: 1 }}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSendAddEmail(); }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={addEmailLoading ? null : <AddIcon />}
                                onClick={handleSendAddEmail}
                                disabled={addEmailLoading || !newEmailInput.trim()}
                            >
                                {addEmailLoading ? <CircularProgress size={22} /> : 'Add Email'}
                            </Button>
                        </>
                    )}

                    {/* Default Currency */}
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        {t('default_currency')}
                    </Typography>
                    <TextField
                        select
                        fullWidth
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        margin="normal"
                        disabled={isLoading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AttachMoneyIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    >
                        {CURRENCIES.map((c) => (
                            <MenuItem key={c.code} value={c.code}>
                                {c.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Divider sx={{ my: 3 }} />

                    {/* Devices & Sessions Section */}
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DevicesIcon fontSize="small" color="action" />
                        {t('devices_sessions') !== 'devices_sessions' ? t('devices_sessions') : 'Devices & Sessions'}
                    </Typography>
                    <FormControlLabel
                        sx={{ alignItems: 'flex-start', ml: 0 }}
                        control={
                            <Switch
                                checked={singleDevice}
                                onChange={handleToggleSingleDevice}
                                disabled={sessionSaving}
                            />
                        }
                        label={
                            <Box sx={{ ml: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Restrict to a single device
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    When on, signing in on a new device logs you out everywhere else.
                                    Turn off to stay signed in on multiple devices at once.
                                </Typography>
                            </Box>
                        }
                    />
                    {sessionSaving && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" color="text.secondary">Saving…</Typography>
                        </Box>
                    )}
                    {sessionError && (
                        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setSessionError('')}>
                            {sessionError}
                        </Alert>
                    )}

                    <Divider sx={{ my: 3 }} />

                    {/* Password Reset Section */}
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        {t('change_password')}
                    </Typography>
                    
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label={t('new_password')}
                            type="password"
                            value={newPw}
                            onChange={(e) => setNewPw(e.target.value)}
                            margin="normal"
                            disabled={pwLoading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlinedIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            label={t('confirm_new_password')}
                            type="password"
                            value={confirmPw}
                            onChange={(e) => setConfirmPw(e.target.value)}
                            margin="normal"
                            disabled={pwLoading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlinedIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {pwError && (
                            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setPwError('')}>{pwError}</Alert>
                        )}
                        {pwSuccess && (
                            <Alert severity="success" sx={{ mt: 1 }}>{pwSuccess}</Alert>
                        )}
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleChangePassword}
                                disabled={pwLoading || !newPw || !confirmPw}
                                startIcon={pwLoading ? <CircularProgress size={18} /> : <LockOutlinedIcon />}
                            >
                                {t('save')}
                            </Button>
                        </Box>
                    </Box>

                    {/* WhatsApp Linking Section — only for username users without a phone number */}
                    {isUsernameUser && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                                {t('link_whatsapp_title')}
                            </Typography>

                            <>
                                    {linkStep === 'idle' && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {t('link_whatsapp_desc')}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                startIcon={<WhatsAppIcon sx={{ color: '#25d366' }} />}
                                                onClick={() => setLinkStep('phone')}
                                            >
                                                {t('link_whatsapp_btn')}
                                            </Button>
                                        </Box>
                                    )}

                                    {linkStep === 'phone' && (
                                        <Box>
                                            <TextField
                                                fullWidth
                                                label={t('enter_whatsapp_number')}
                                                type="tel"
                                                value={waPhone}
                                                onChange={(e) => setWaPhone(e.target.value)}
                                                placeholder="+1234567890"
                                                disabled={linkLoading}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <WhatsAppIcon sx={{ color: '#25d366' }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{ mb: 2 }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleSendWhatsAppCode}
                                                    disabled={linkLoading || !waPhone.trim()}
                                                >
                                                    {linkLoading ? <CircularProgress size={20} /> : t('send_verification_code')}
                                                </Button>
                                                <Button
                                                    variant="text"
                                                    onClick={() => { setLinkStep('idle'); setWaPhone(''); setLinkError(''); }}
                                                    disabled={linkLoading}
                                                >
                                                    {t('cancel_btn')}
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}

                                    {linkStep === 'code' && (
                                        <Box>
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                {t('verification_code_sent_whatsapp')}
                                            </Alert>
                                            <TextField
                                                fullWidth
                                                label={t('enter_verification_code')}
                                                value={waCode}
                                                onChange={(e) => setWaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                placeholder="000000"
                                                disabled={linkLoading}
                                                inputProps={{
                                                    maxLength: 6,
                                                    style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px', fontWeight: 'bold' }
                                                }}
                                                sx={{ mb: 2 }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleVerifyWhatsAppCode}
                                                    disabled={linkLoading || waCode.length !== 6}
                                                >
                                                    {linkLoading ? <CircularProgress size={20} /> : t('verify_code')}
                                                </Button>
                                                <Button
                                                    variant="text"
                                                    onClick={() => { setLinkStep('phone'); setWaCode(''); setLinkError(''); }}
                                                    disabled={linkLoading}
                                                >
                                                    {t('back')}
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                            </>

                            {linkError && (
                                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setLinkError('')}>
                                    {linkError}
                                </Alert>
                            )}
                            {linkSuccess && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    {linkSuccess}
                                </Alert>
                            )}
                        </>
                    )}

                    {/* Error and Success messages */}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            {success}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    {t('close')}
                </Button>
                <Button
                    onClick={handleSaveProfile}
                    variant="contained"
                    disabled={isLoading}
                >
                    {isLoading ? <CircularProgress size={24} /> : t('save')}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Popup dialog: email already linked to a different account */}
        <Dialog
            open={emailConflictDialog.open}
            onClose={() => setEmailConflictDialog({ open: false, email: '' })}
            maxWidth="xs"
            fullWidth
            sx={{ zIndex: 1400 }}
        >
            <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
                <WarningAmberRoundedIcon sx={{ fontSize: 56, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                    Email Already Registered
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    The email address{' '}
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                        {emailConflictDialog.email}
                    </Typography>
                    {' '}is already associated with a different account.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Please use a different email address.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button
                    variant="contained"
                    onClick={() => setEmailConflictDialog({ open: false, email: '' })}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default ProfileSettings;
